// OPHORUS - IA TRAVEL ASSISTANT "Modula"
//
// Panneau lateral non bloquant, ouvert depuis l'icone "Find your trip" du
// menu desktop ou le CTA "Inspire me" de la homepage (tous deux repris tels
// quels, cf. data-assistant-open).
//
// Architecture (cf. refonte UX) : le chat PILOTE une selection persistante de
// produits plutot que d'accumuler des fiches dans l'historique.
//   - state.searchState   : criteres ajoutes explicitement par l'utilisateur
//                           (chips "Your preferences" / suggestions / +Add),
//                           cumulatifs d'un message a l'autre. L'IA elle-meme
//                           comprend le langage libre cote serveur, ce state
//                           ne sert qu'a l'affichage des chips.
//   - state.currentResults: la selection de produits actuelle, TOUJOURS
//                           remplacee (jamais empilee) a chaque reponse avec
//                           des tours. Vit dans l'onglet Recommendations.
//   - state.messages      : uniquement la trace conversationnelle (bulles,
//                           statuts, apercu leger) - jamais les fiches
//                           produits completes.
//
// fetchAssistantReply() appelle le vrai backend (endpoint "modula-chat",
// cf. Controller_ophorus_modula::chat() / Model_modula, IA_ASSISTANT_TRIP_JOURNEY.md).
// L'historique de conversation cote Anthropic est garde en session PHP, pas
// renvoye par le client.
import $ from 'jquery';
import mobile from './mobile';
import { ajaxRequest } from './ajax';

// ---------------------------------------------------------------------
// DONNEES STATIQUES (suggestions / picker de criteres)
// ---------------------------------------------------------------------

// Suggestions initiales - chacune porte directement les criteres qu'elle
// exprime, pour peupler tout de suite les chips "Your preferences".
const SUGGESTIONS = [
	{
		label: 'Recommend a family-friendly tour near Lyon',
		criteria: [{ key: 'destination', label: 'Lyon' }, { key: 'audience', label: 'Family friendly' }],
	},
	{
		label: 'Find a day trip from Nice',
		criteria: [{ key: 'destination', label: 'Nice' }, { key: 'duration', label: 'Full day' }],
	},
	{
		label: 'Show accessible tours',
		criteria: [{ key: 'audience', label: 'Accessible' }],
	},
	{
		label: 'Suggest a food and wine experience',
		criteria: [{ key: 'theme', label: 'Food & Wine' }],
	},
];

// Reservoir de criteres proposables pour les quick actions "Try" (le popover
// "+ Add" a son propre reservoir plus complet, cf. ADD_CRITERIA_CATEGORIES) -
// on ne propose que les cles pas encore dans searchState, cf. getQuickActions().
const CRITERIA_POOL = [
	{ key: 'budget', label: 'Under €100', addLabel: 'Cheaper options' },
	{ key: 'duration', label: 'Half day', addLabel: 'Half day' },
	{ key: 'date', label: 'Tomorrow', addLabel: 'Available tomorrow' },
	{ key: 'type', label: 'Private tour', addLabel: 'Private tour' },
	{ key: 'theme', label: 'Food & Wine', addLabel: 'Food & Wine' },
];

// Actions contextuelles de l'etat "aucun resultat" - assouplissent un critere
// existant plutot que d'en ajouter un.
const NO_RESULT_ACTIONS = [
	{ label: 'Increase budget', removeKey: 'budget' },
	{ label: 'Change date', removeKey: 'date' },
	{ label: 'Remove duration', removeKey: 'duration' },
];

// Categories completes du popover "+ Add" (cf. brief : plus d'input libre au
// milieu des chips - un picker structure categorie -> valeur a la place).
// Volontairement distinct de CRITERIA_POOL (reserve aux suggestions "Try",
// courtes et contextuelles) : ce popover doit couvrir tous les types de
// criteres possibles, pas juste les plus probables.
const ADD_CRITERIA_CATEGORIES = [
	{ key: 'destination', label: 'Destination', options: ['Lyon', 'Nice', 'Bordeaux', 'Paris', 'Provence'] },
	{ key: 'date', label: 'Date', options: ['Today', 'Tomorrow', 'This weekend', 'Next week'] },
	{ key: 'duration', label: 'Duration', options: ['Half day', 'Full day', 'Multi-day'] },
	{ key: 'budget', label: 'Budget', options: ['Under €100', '€100 - €200', '€200+'] },
	{ key: 'participants', label: 'Participants', options: ['Solo', 'Couple', 'Family', 'Group'] },
	{ key: 'experience', label: 'Experience type', options: ['Private tour', 'Shared tour', 'Food & Wine', 'Adventure', 'Culture'] },
	{ key: 'language', label: 'Language', options: ['English', 'French', 'Spanish'] },
	{ key: 'transportation', label: 'Transportation', options: ['Walking', 'Private car', 'Coach', 'Self-drive'] },
];

// Nom de la destination/region/categorie de la page courante, si disponible
// proprement : "h1._big" est le titre principal partage par TOUTES les pages
// de resultats du site (destination, region, categorie, shore...), cf.
// application/views/site/PAGES/RESULTS/*_header.php - pas de nouvelle donnee
// PHP a brancher, juste lire ce qui est deja affiche a l'ecran. "Handpicked
// for you" (page resultats generique, pas de lieu precis) est exclu.
function currentPageContext() {
	const label = $('h1._big').first().text().trim();
	return label && label !== 'Handpicked for you' ? label : null;
}

// Suggestions initiales adaptees au lieu de la page courante (cf. brief
// polish section 7) - evite de proposer Lyon/Nice par defaut sur une page
// Marseille par exemple.
function contextualSuggestions(place) {
	return [
		{ label: `Find a tour in ${place}`, criteria: [{ key: 'destination', label: place }] },
		{ label: `Family-friendly tours in ${place}`, criteria: [{ key: 'destination', label: place }, { key: 'audience', label: 'Family friendly' }] },
		{ label: `What to do around ${place}?`, criteria: [{ key: 'destination', label: place }] },
	];
}

const assistant = {
	$panel: null,
	$backdrop: null,
	$subtitle: null,
	$tabChat: null,
	$tabTours: null,
	$panelChat: null,
	$panelTours: null,
	$thread: null,
	$toursBadge: null,
	$toursHeader: null,
	$toursContent: null,
	$form: null,
	$input: null,
	$send: null,
	$composerTop: null,
	fallbackImg: '',

	isOpen: false,
	hasWelcomed: false,
	lastTrigger: null,
	_notifyTimeout: null,

	// Etat de la conversation. `messages` ne contient jamais de fiches
	// produits completes - `currentResults` est toujours remplace, jamais
	// empile. `searchState` est la memoire cumulative des criteres compris.
	state: {
		activeTab: 'chat',
		messages: [],
		searchState: [],
		currentResults: [],
		activeFilters: [],
		hasSearched: false,
		isLoading: false,
		error: null,
	},

	init() {
		this.$panel = $('#ophorus_assistant');
		if (!this.$panel.length) {
			return;
		}

		this.$backdrop = $('[data-assistant-backdrop]');
		this.$subtitle = this.$panel.find('#assistant_subtitle');
		this.$tabChat = this.$panel.find('#assistant_tab_chat');
		this.$tabTours = this.$panel.find('#assistant_tab_tours');
		this.$panelChat = this.$panel.find('#assistant_panel_chat');
		this.$panelTours = this.$panel.find('#assistant_panel_tours');
		this.$thread = this.$panel.find('[data-assistant-thread]');
		this.$toursBadge = this.$panel.find('[data-assistant-tours-badge]');
		this.$toursHeader = this.$panel.find('[data-assistant-tours-header]');
		this.$toursContent = this.$panel.find('[data-assistant-tours-content]');
		this.$form = this.$panel.find('[data-assistant-form]');
		this.$input = this.$panel.find('[data-assistant-input]');
		this.$send = this.$panel.find('[data-assistant-send]');
		this.$composerTop = this.$panel.find('[data-composer-top]');
		this.fallbackImg = this.$panel.data('assistant-fallback-img') || '';

		this.bindEvents();
	},

	bindEvents() {
		// Declencheurs : icone "Find your trip" du menu + CTA "Inspire me" de
		// la homepage (cf. data-assistant-open, markup existant non modifie).
		$(document).on('click', '[data-assistant-open]', (e) => {
			this.lastTrigger = e.currentTarget;
			this.openWithContext(e.currentTarget);
		});

		$(document).on('keydown', '[data-assistant-open]', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				this.lastTrigger = e.currentTarget;
				this.openWithContext(e.currentTarget);
			}
		});

		$(document).on('click', '[data-assistant-close], [data-assistant-backdrop]', () => {
			this.close();
		});

		$(document).on('keydown', (e) => {
			if (e.key !== 'Escape' || !this.isOpen) {
				return;
			}
			if (this.$thread.find('[data-add-popover]:not([hidden])').length) {
				this.closeAllAddPopovers();
				return;
			}
			this.close();
		});

		// Onglets Chat / Recommendations - clic + navigation clavier gauche/droite
		// (pattern ARIA tabs).
		$(document).on('click', '[data-assistant-tab]', (e) => {
			this.setActiveTab($(e.currentTarget).data('assistant-tab'));
		});

		$(document).on('keydown', '[data-assistant-tab]', (e) => {
			if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
				return;
			}
			e.preventDefault();
			const next = e.key === 'ArrowRight' ? 'tours' : 'chat';
			this.setActiveTab(next);
			(next === 'chat' ? this.$tabChat : this.$tabTours).trigger('focus');
		});

		// CTA de l'etat vide de l'onglet Recommendations ("Start a conversation").
		$(document).on('click', '[data-start-conversation]', () => {
			this.setActiveTab('chat');
			this.focusMessageInput();
		});

		// Bouton "View N matching tours" affiche apres chaque reponse - bascule
		// vers Recommendations sans jamais dupliquer les cartes dans le chat.
		$(document).on('click', '[data-view-tours]', () => {
			this.setActiveTab('tours');
		});

		// "Start over" - efface la conversation et la selection en cours.
		this.$panel.find('[data-start-over]').on('click', () => this.resetConversation());

		// Textarea : Entree envoie, Shift+Entree fait une nouvelle ligne,
		// auto-grandissement jusqu'a max-height (cf. max-height en SCSS).
		this.$input.on('input', () => {
			this.autosizeInput();
			this.$send.prop('disabled', this.$input.val().trim() === '');
		});

		this.$input.on('keydown', (e) => {
			if (e.key === 'Enter' && !e.shiftKey) {
				e.preventDefault();
				this.$form.trigger('submit');
			}
		});

		this.$form.on('submit', (e) => {
			e.preventDefault();
			const message = this.$input.val().trim();
			if (!message || this.state.isLoading) {
				return;
			}
			this.sendUserMessage(message);
		});

		// Suggestions initiales - delegation car injectees dynamiquement a
		// chaque renderChat().
		this.$thread.on('click', '[data-suggestions] .__chip', (e) => {
			if (this.state.isLoading) {
				return;
			}
			const $chip = $(e.currentTarget);
			const entry = this.state.messages[Number($chip.closest('[data-suggestions]').data('msg-index'))];
			const item = entry.items[Number($chip.data('item-index'))];
			entry.used = true;
			this.sendUserMessage(item.label, item.criteria);
		});

		// Quick actions contextuelles ("Try ...") - ajoutent un critere et
		// relancent une recherche. Les actions de l'etat "aucun resultat"
		// (data-remove-key) assouplissent un critere existant a la place.
		this.$thread.on('click', '[data-quick-actions] .__chip', (e) => {
			if (this.state.isLoading) {
				return;
			}
			const $chip = $(e.currentTarget);
			const entry = this.state.messages[Number($chip.closest('[data-quick-actions]').data('msg-index'))];
			const action = entry.actions[Number($chip.data('item-index'))];
			if (action.removeKey) {
				this.state.searchState = this.state.searchState.filter((c) => c.key !== action.removeKey);
				this.sendUserMessage(action.label, []);
			} else {
				this.sendUserMessage(action.label, [{ key: action.key, label: action.label2 || action.label }]);
			}
		});

		// "Your preferences" : suppression d'un critere (relance une recherche
		// avec le reste des criteres) + picker "+ Add" pour en ajouter un.
		// Lit toujours l'etat live (this.state.searchState), pas une copie
		// figee dans le message - cf. buildPreferencesEl().
		this.$thread.on('click', '[data-preferences] [data-remove-criterion]', (e) => {
			if (this.state.isLoading) {
				return;
			}
			const key = $(e.currentTarget).closest('[data-key]').data('key');
			this.state.searchState = this.state.searchState.filter((c) => c.key !== key);
			this.sendUserMessage(`Remove ${key}`, []);
		});

		// "+ Add" : popover 2 etapes (categorie -> valeur), cf. brief section 5.
		// Un seul popover ouvert a la fois (closeAllAddPopovers ferme les autres
		// avant d'ouvrir celui-ci, y compris les instances passees de la meme
		// liste apres un re-render).
		this.$thread.on('click', '[data-add-criteria-toggle]', (e) => {
			e.stopPropagation();
			const $btn = $(e.currentTarget);
			const $popover = $btn.closest('._add_wrap').find('[data-add-popover]');
			// ".is('[hidden]')" plutot que "!.attr('hidden')" : l'attribut brut
			// du HTML initial vaut "" (falsy) alors que closeAllAddPopovers()
			// lui donne "true" (truthy) - un simple "!attr()" donnait donc un
			// resultat different selon que le popover ait deja ete bascule une
			// fois ou non, cassant le tout premier clic apres chaque re-render.
			const wasOpen = !$popover.is('[hidden]');
			this.closeAllAddPopovers();
			if (!wasOpen) {
				this.openAddPopover($popover, $btn);
			}
		});

		this.$thread.on('click', '[data-add-category-list] [data-category-index]', (e) => {
			const $popover = $(e.currentTarget).closest('[data-add-popover]');
			const category = ADD_CRITERIA_CATEGORIES[Number($(e.currentTarget).data('category-index'))];
			this.renderAddPopoverValues($popover, category);
		});

		this.$thread.on('click', '[data-add-back]', (e) => {
			this.renderAddPopoverCategories($(e.currentTarget).closest('[data-add-popover]'));
		});

		this.$thread.on('click', '[data-add-value-list] [data-option-value]', (e) => {
			if (this.state.isLoading) {
				return;
			}
			const $popover = $(e.currentTarget).closest('[data-add-popover]');
			const category = ADD_CRITERIA_CATEGORIES.find((c) => c.key === $popover.attr('data-category-key'));
			const value = $(e.currentTarget).text();
			this.closeAllAddPopovers();
			this.sendUserMessage(`${category.label}: ${value}`, [{ key: category.key, label: value }]);
		});

		// Clic en dehors / Echap : ferme le popover ouvert (pas tout le
		// panneau - cf. handler Escape plus haut, ajuste pour verifier ca
		// d'abord).
		$(document).on('click', (e) => {
			if (!$(e.target).closest('._add_wrap').length) {
				this.closeAllAddPopovers();
			}
		});
	},

	// ---------------------------------------------------------------
	// OUVERTURE / FERMETURE DU PANNEAU
	// ---------------------------------------------------------------

	open() {
		if (this.isOpen) {
			return;
		}
		this.isOpen = true;

		mobile.closeMobileMenu();
		mobile.closeBasket();
		mobile.closeBookingSheet();

		this.$panel.addClass('_open').attr('aria-hidden', 'false');
		this.$backdrop.addClass('_show');
		$('[data-assistant-open]').attr('aria-expanded', 'true');
		$('html,body').addClass('_overflow');

		if (!this.hasWelcomed) {
			this.renderWelcome();
			this.renderTours();
			this.hasWelcomed = true;
		}

		this.focusMessageInput();
	},

	openWithContext(trigger) {
		const $el = $(trigger);
		const prompt = $el.data('assistant-prompt');
		const criteria = $el.data('assistant-criteria');

		if (!prompt) {
			this.open();
			return;
		}

		this.state.isLoading = false;
		this.state.messages = [];
		this.state.searchState = [];
		this.state.currentResults = [];
		this.state.activeFilters = [];
		this.state.hasSearched = false;
		this.state.error = null;
		this.hasWelcomed = false;
		ajaxRequest(`${_SITE_URL}modula-chat`, { reset: 1 }).catch(() => {});

		this.open();

		if (!this.hasWelcomed) {
			this.renderWelcome();
			this.renderTours();
			this.renderTourCount();
			this.updateHeaderSubtitle();
			this.hasWelcomed = true;
		}

		this.sendUserMessage(prompt, criteria || []);
	},

	close() {
		if (!this.isOpen) {
			return;
		}
		this.isOpen = false;

		this.$panel.removeClass('_open').attr('aria-hidden', 'true');
		this.$backdrop.removeClass('_show');
		$('[data-assistant-open]').attr('aria-expanded', 'false');
		$('html,body').removeClass('_overflow');

		// Rend le focus au bouton qui a ouvert le panneau (clavier).
		if (this.lastTrigger) {
			$(this.lastTrigger).trigger('focus');
		}
	},

	resetConversation() {
		ajaxRequest(`${_SITE_URL}modula-chat`, { reset: 1 }).catch(() => {});
		this.state.messages = [];
		this.state.searchState = [];
		this.state.currentResults = [];
		this.state.activeFilters = [];
		this.state.hasSearched = false;
		this.state.error = null;
		this.renderWelcome();
		this.renderTours();
		this.renderTourCount();
		this.updateHeaderSubtitle();
		this.setActiveTab('chat');
		this.focusMessageInput();
	},

	// ---------------------------------------------------------------
	// ONGLETS
	// ---------------------------------------------------------------

	setActiveTab(tab) {
		if (this.state.activeTab === tab) {
			if (tab === 'tours') {
				this.scrollToursToTop();
			}
			return;
		}
		this.state.activeTab = tab;

		const isChat = tab === 'chat';
		this.$tabChat
			.toggleClass('_active', isChat)
			.attr('aria-selected', String(isChat))
			.attr('tabindex', isChat ? '0' : '-1');
		this.$tabTours
			.toggleClass('_active', !isChat)
			.attr('aria-selected', String(!isChat))
			.attr('tabindex', !isChat ? '0' : '-1');
		this.$panelChat.toggleClass('_active', isChat).prop('hidden', !isChat);
		this.$panelTours.toggleClass('_active', !isChat).prop('hidden', isChat);

		if (isChat) {
			this.scrollChatToBottom();
		} else {
			this.scrollToursToTop();
			// L'utilisateur regarde l'onglet : plus besoin d'attirer son oeil.
			clearTimeout(this._notifyTimeout);
			this.$tabTours.add(this.$toursBadge).removeClass('_notify');
		}
	},

	// ---------------------------------------------------------------
	// COMPOSER / SCROLL / FOCUS
	// ---------------------------------------------------------------

	autosizeInput() {
		const el = this.$input.get(0);
		if (!el) {
			return;
		}
		el.style.height = 'auto';
		el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
	},

	scrollChatToBottom() {
		const el = this.$panel.find('[data-assistant-chat-scroll]').get(0);
		if (el) {
			el.scrollTop = el.scrollHeight;
		}
	},

	scrollToursToTop() {
		const el = this.$panel.find('[data-assistant-tours-scroll]').get(0);
		if (el) {
			el.scrollTop = 0;
		}
	},

	focusMessageInput() {
		this.$input.trigger('focus');
	},

	// En-tete : remplace le sous-titre statique par une info utile et
	// dynamique ("N recommendations"), masque tant qu'il n'y a rien a montrer.
	updateHeaderSubtitle() {
		const count = this.state.currentResults.length;
		if (!count) {
			this.$subtitle.text('').attr('hidden', true);
			this.$composerTop.attr('hidden', true);
			return;
		}
		this.$subtitle.text(`${count} recommendation${count === 1 ? '' : 's'}`).removeAttr('hidden');
		this.$composerTop.removeAttr('hidden');
	},

	// ---------------------------------------------------------------
	// FLUX CONVERSATION
	// ---------------------------------------------------------------

	sendUserMessage(text, explicitCriteria) {
		if (this.state.isLoading) {
			return;
		}
		if (this.state.activeTab !== 'chat') {
			this.setActiveTab('chat');
		}

		this.addMessage({ kind: 'text', role: 'user', text });

		this.$input.val('').trigger('input');
		this.autosizeInput();

		if (explicitCriteria && explicitCriteria.length) {
			this.state.searchState = this.mergeCriteria(this.state.searchState, explicitCriteria);
		}

		this.state.isLoading = true;
		this.$send.prop('disabled', true);
		this.renderChat();
		this.renderTours();
		this.scrollChatToBottom();

		this.fetchAssistantReply(text).then((reply) => {
			this.state.isLoading = false;
			this.handleAssistantReply(reply);
			this.$send.prop('disabled', this.$input.val().trim() === '');
			if (this.state.activeTab === 'chat') {
				this.focusMessageInput();
			}
		});
	},

	// Cumule les nouveaux criteres sur l'existant (remplace par cle) - c'est
	// ce qui permet a "something cheaper" de garder Lyon + Family et
	// d'ajouter juste le budget, plutot que de repartir de zero. Ces criteres
	// ne pilotent que l'affichage des chips "Your preferences" - la recherche
	// elle-meme est comprise en langage libre par Modula cote serveur.
	mergeCriteria(existing, additions) {
		if (!additions.length) {
			return existing;
		}
		const kept = existing.filter((c) => !additions.some((a) => a.key === c.key));
		return [...kept, ...additions];
	},

	// Appelle le vrai backend (Controller_ophorus_modula::chat()) - l'historique
	// de conversation Anthropic est garde en session PHP, pas renvoye ici.
	async fetchAssistantReply(text) {
		try {
			const response = await ajaxRequest(`${_SITE_URL}modula-chat`, { message: text });
			if (response && response.error) {
				return { error: true, message: response.message };
			}
			return response;
		} catch (e) {
			return { error: true, message: 'Something went wrong. Please try again.' };
		}
	},

	handleAssistantReply(reply) {
		if (!reply || reply.error) {
			this.addMessage({ kind: 'error', text: (reply && reply.message) || 'Something went wrong. Please try again.' });
			// Efface le bandeau "Updating tours..." sans toucher aux resultats
			// actifs - une erreur technique ne doit pas vider Recommendations.
			this.renderTours();
			return;
		}

		// Remplace entierement les chips par ce que Modula a reellement compris
		// pour cette recherche - source de verite serveur, jamais fusionnee
		// avec l'ancien etat (cf. Model_modula::buildCriteriaChips()). Absent
		// (pas un tableau) quand Claude n'a pas relance de recherche ce tour-ci
		// (ex: question de clarification) - dans ce cas on garde les chips
		// existantes telles quelles.
		if (Array.isArray(reply.criteria)) {
			this.state.searchState = reply.criteria;
		}

		if (!reply.tours || !reply.tours.length) {
			this.addMessages([
				{ kind: 'text', role: 'assistant', text: reply.message || "I couldn't find a tour matching your request." },
				{ kind: 'preferences' },
				{ kind: 'quick_actions', label: 'Try', actions: NO_RESULT_ACTIONS },
			]);
			this.renderTours();
			return;
		}

		// Une seule liste de tours visible a la fois dans le fil - la nouvelle
		// remplace l'ancienne au lieu de s'empiler en scrollant vers le haut
		// (cf. retour utilisateur : la reponse du moment doit toujours porter
		// ses propres cartes, pas une liste perimee laissee plus haut).
		this.state.messages = this.state.messages.filter((m) => m.kind !== 'tour_mini_list');

		const entries = [
			{ kind: 'text', role: 'assistant', text: reply.message || '' },
			{ kind: 'tour_mini_list', tours: reply.tours },
			{ kind: 'preferences' },
		];
		const quickActions = this.getQuickActions();
		if (quickActions.length) {
			entries.push({ kind: 'quick_actions', label: 'Try', actions: quickActions });
		}
		this.addMessages(entries);

		this.updateTourResults(reply.tours, this.state.searchState.map((c) => c.label));
	},

	// Jusqu'a 3 raccourcis contextuels, uniquement sur des criteres pas
	// encore choisis (cf. brief : "ne doit pas afficher toujours exactement
	// les memes suggestions").
	getQuickActions() {
		const usedKeys = new Set(this.state.searchState.map((c) => c.key));
		return CRITERIA_POOL
			.filter((c) => !usedKeys.has(c.key))
			.slice(0, 3)
			.map((c) => ({ label: c.addLabel, key: c.key, label2: c.label }));
	},

	// Categories du popover "+ Add" pas encore actives - une fois choisie,
	// une categorie rejoint "Your preferences" et disparait du popover (pour
	// la remettre a disposition, il faut d'abord retirer son chip actif).
	getAddableCategories() {
		const usedKeys = new Set(this.state.searchState.map((c) => c.key));
		return ADD_CRITERIA_CATEGORIES.filter((c) => !usedKeys.has(c.key));
	},

	// ---------------------------------------------------------------
	// ETAT -> RENDU : CHAT
	// ---------------------------------------------------------------

	renderWelcome() {
		const place = currentPageContext();
		this.addMessages([
			{
				kind: 'text',
				role: 'assistant',
				text: place
					? `Hello! I can help you find the perfect tour in ${place}.\nWhat are you looking for?`
					: 'Hello! I can help you find the perfect tour.\nWhere would you like to go?',
			},
			{ kind: 'suggestions', items: place ? contextualSuggestions(place) : SUGGESTIONS, used: false },
		]);
	},

	addMessage(entry) {
		this.addMessages([entry]);
	},

	addMessages(entries) {
		this.state.messages.push(...entries);
		this.renderChat();
		this.scrollChatToBottom();
	},

	renderChat() {
		this.$thread.empty();
		this.state.messages.forEach((entry, index) => {
			this.$thread.append(this.buildMessageEl(entry, index));
		});
		if (this.state.isLoading) {
			this.$thread.append(this.buildLoaderEl());
		}
	},

	buildMessageEl(entry, index) {
		switch (entry.kind) {
			case 'text':
				return $(`<li class="_msg _${entry.role}"></li>`).text(entry.text);

			case 'suggestions':
				return this.buildSuggestionsEl(entry, index);

			case 'quick_actions':
				return this.buildQuickActionsEl(entry, index);

			case 'preferences':
				return this.buildPreferencesEl();

			case 'tour_mini_list':
				return this.buildTourMiniListEl(entry.tours);

			case 'error':
				return $('<li class="_error"></li>').text(entry.text);

			default:
				return $();
		}
	},

	buildSuggestionsEl(entry, index) {
		if (entry.used) {
			return $();
		}
		const $li = $('<li class="_chip_group" data-suggestions></li>').attr('data-msg-index', index);
		$('<span class="_chip_label">Suggestions</span>').appendTo($li);
		const $row = $('<div class="_chips"></div>').appendTo($li);
		entry.items.forEach((item, itemIndex) => {
			$('<button type="button" class="__chip"></button>')
				.attr('data-item-index', itemIndex)
				.text(item.label)
				.appendTo($row);
		});
		return $li;
	},

	buildQuickActionsEl(entry, index) {
		const $li = $('<li class="_chip_group" data-quick-actions></li>').attr('data-msg-index', index);
		$('<span class="_chip_label"></span>').text(entry.label).appendTo($li);
		const $row = $('<div class="_chips"></div>').appendTo($li);
		entry.actions.forEach((action, itemIndex) => {
			$('<button type="button" class="__chip"></button>')
				.attr('data-item-index', itemIndex)
				.text(action.label)
				.appendTo($row);
		});
		return $li;
	},

	// "Your preferences" : memoire visible des criteres compris par l'IA.
	// Lit toujours this.state.searchState en direct (pas une copie figee sur
	// l'entree) pour que TOUTES les occurrences dans l'historique refletent
	// l'etat reel actuel, meme les plus anciennes.
	// "Your preferences" ne montre QUE les criteres actifs (jamais de
	// suggestion) - cf. brief section 4, a ne jamais melanger avec "Try".
	buildPreferencesEl() {
		const $li = $('<li class="_preferences" data-preferences></li>');
		$('<span class="_chip_label">Your preferences</span>').appendTo($li);
		const $row = $('<div class="_chips"></div>').appendTo($li);

		this.state.searchState.forEach((criterion) => {
			const $chip = $('<span class="__chip _pref_chip"></span>').attr('data-key', criterion.key);
			$('<span></span>').text(criterion.label).appendTo($chip);
			$('<button type="button" data-remove-criterion aria-label="Remove"></button>').html('&times;').appendTo($chip);
			$row.append($chip);
		});

		const $addWrap = $('<div class="_add_wrap"></div>').appendTo($row);
		$('<button type="button" class="__chip _add_chip" data-add-criteria-toggle aria-haspopup="true" aria-expanded="false">+ Add</button>').appendTo($addWrap);
		const $popover = $('<div class="_add_popover" data-add-popover hidden></div>').appendTo($addWrap);
		$('<div class="_add_popover_list" data-add-category-list></div>').appendTo($popover);
		$('<div class="_add_popover_values" data-add-value-list hidden></div>').appendTo($popover);

		return $li;
	},

	closeAllAddPopovers() {
		this.$thread.find('[data-add-popover]').attr('hidden', true);
		this.$thread.find('[data-add-criteria-toggle]').attr('aria-expanded', 'false');
	},

	openAddPopover($popover, $btn) {
		this.renderAddPopoverCategories($popover);
		$popover.removeAttr('hidden');
		$btn.attr('aria-expanded', 'true');
	},

	// Etape 1 du popover : liste des categories pas encore actives.
	renderAddPopoverCategories($popover) {
		$popover.removeAttr('data-category-key');
		$popover.find('[data-add-value-list]').empty().attr('hidden', true);

		const $list = $popover.find('[data-add-category-list]').empty().removeAttr('hidden');
		const categories = this.getAddableCategories();
		if (!categories.length) {
			$('<p class="_add_popover_empty"></p>').text('All set - nothing left to add.').appendTo($list);
			return;
		}
		categories.forEach((category) => {
			$('<button type="button" class="_add_popover_item"></button>')
				.attr('data-category-index', ADD_CRITERIA_CATEGORIES.indexOf(category))
				.text(category.label)
				.appendTo($list);
		});
	},

	// Etape 2 : valeurs de la categorie choisie, avec retour a la liste.
	renderAddPopoverValues($popover, category) {
		$popover.attr('data-category-key', category.key);
		$popover.find('[data-add-category-list]').attr('hidden', true);

		const $values = $popover.find('[data-add-value-list]').empty().removeAttr('hidden');
		const $back = $('<button type="button" class="_add_popover_back" data-add-back></button>').appendTo($values);
		$back.append('<svg class="_ic_lucide" aria-hidden="true"><use xlink:href="#__ic_chevron_down_lucide"/></svg>');
		$back.append($('<span></span>').text(category.label));

		category.options.forEach((option) => {
			$('<button type="button" class="_add_popover_item" data-option-value></button>')
				.text(option)
				.appendTo($values);
		});
	},

	// Apercu leger des tours trouves directement dans le chat (thumbnail +
	// titre + duree + prix, sans carte complete - celles-ci restent
	// exclusives a l'onglet Recommendations, cf. brief section 6/20).
	buildTourMiniListEl(tours) {
		const $li = $('<li class="_tour_mini_list"></li>');
		const $ul = $('<ul></ul>').appendTo($li);
		tours.forEach((tour) => {
			const $item = $('<li></li>');
			const $link = $('<a></a>').attr('href', tour.url || '#');
			$('<span class="__thumb"><img alt=""></span>')
				.toggleClass('_no_photo', !tour.img)
				.appendTo($link)
				.find('img').attr('src', tour.img || this.fallbackImg);
			const $text = $('<span class="_mini_text"></span>').appendTo($link);
			$('<span class="_mini_title"></span>').text(tour.title).appendTo($text);
			$('<span class="_mini_meta"></span>').text(`${tour.duration} · From €${tour.price}`).appendTo($text);
			$item.append($link);
			$ul.append($item);
		});
		$('<button type="button" class="_view_tours_btn" data-view-tours></button>')
			.text(`View ${tours.length} matching tour${tours.length === 1 ? '' : 's'}`)
			.appendTo($li);
		return $li;
	},

	buildLoaderEl() {
		return $(
			'<li class="_loader" data-loader>' +
				'<span class="_dots"><span></span><span></span><span></span></span>' +
				'<span>Finding the best tours…</span>' +
			'</li>'
		);
	},

	// ---------------------------------------------------------------
	// ETAT -> RENDU : RECOMMENDATIONS (toujours remplace, jamais empile)
	// ---------------------------------------------------------------

	updateTourResults(results, filters) {
		this.state.currentResults = results;
		this.state.activeFilters = filters || [];
		this.state.hasSearched = true;
		this.state.error = null;
		this.renderTours();
		this.renderTourCount();
		this.updateHeaderSubtitle();

		// Signal visuel sur l'onglet quand les resultats changent pendant que
		// l'utilisateur est reste sur Chat, pour ne pas le manquer.
		if (this.state.activeTab !== 'tours') {
			this.notifyToursTab();
		}
	},

	notifyToursTab() {
		const $targets = this.$tabTours.add(this.$toursBadge);
		$targets.removeClass('_notify');
		// Force le reflow pour pouvoir relancer l'animation si elle vient deja
		// de tourner (ex: deux recherches rapprochees).
		void this.$tabTours.get(0).offsetWidth;
		$targets.addClass('_notify');
		clearTimeout(this._notifyTimeout);
		this._notifyTimeout = setTimeout(() => $targets.removeClass('_notify'), 1300);
	},

	// Header simplifie : une ligne de compte ("3 tours for you") + une ligne
	// de criteres ("Lyon · Family friendly") - plus de 3e ligne redondante
	// (cf. brief section 7, l'ancienne version repetait la meme info sous
	// deux formats juxtaposes).
	renderTours() {
		const { currentResults, activeFilters, hasSearched, isLoading } = this.state;

		this.$toursHeader.empty();
		if (hasSearched) {
			this.$toursHeader.append(
				$('<span class="_tours_count"></span>').text(`${currentResults.length} tour${currentResults.length === 1 ? '' : 's'} for you`)
			);
			if (activeFilters.length) {
				this.$toursHeader.append($('<span class="_tours_criteria"></span>').text(activeFilters.join(' · ')));
			}
		}

		this.$toursContent.empty();

		if (isLoading) {
			this.$toursContent.append(
				$(
					'<div class="_tours_updating">' +
						'<span class="_dots"><span></span><span></span><span></span></span>' +
						'<span>Updating tours…</span>' +
					'</div>'
				)
			);
		}

		if (!hasSearched) {
			this.$toursContent.append(this.buildToursEmptyEl());
			return;
		}

		if (!currentResults.length) {
			this.$toursContent.append(this.buildToursNoMatchEl());
			return;
		}

		this.$toursContent.append(this.buildCardsEl(currentResults));
	},

	renderTourCount() {
		const count = this.state.currentResults.length;
		this.$toursBadge.text(count > 0 ? String(count) : '');
	},

	buildToursEmptyEl() {
		const $el = $('<div class="_tours_empty"></div>');
		$el.append(
			'<svg class="_ic_lucide" aria-hidden="true"><use xlink:href="#__ic_route_lucide"/></svg>',
			'<strong>No tours yet</strong>',
			'<p>Tell us what you are looking for and your matching tours will appear here.</p>'
		);
		$('<button type="button" class="_tours_start_btn" data-start-conversation></button>')
			.text('Start a conversation')
			.appendTo($el);
		return $el;
	},

	buildToursNoMatchEl() {
		const $el = $('<div class="_tours_empty"></div>');
		$el.append(
			'<svg class="_ic_lucide" aria-hidden="true"><use xlink:href="#__ic_route_lucide"/></svg>',
			'<strong>No matching tours</strong>',
			'<p>Try changing one or more criteria.</p>'
		);
		return $el;
	},

	// Carte compacte : titre dominant, une ligne de metadata discrete
	// (destination · type · duree, plus de gros label geographique en
	// majuscules), prix mis en avant sans bloc lourd, CTA "View tour" seul
	// accent violet de la carte. "badge" est facultatif (1 insight IA max).
	buildCardsEl(tours) {
		const $wrap = $('<div class="__assistant_cards"></div>');
		tours.forEach((tour) => {
			const $card = $(
				'<a class="_assistant_card">' +
					'<div class="__thumb"><img alt=""></div>' +
					'<div class="_card_content">' +
						'<span class="_card_title"></span>' +
						'<span class="_card_meta_row">' +
							'<span class="_card_meta"></span>' +
						'</span>' +
						'<span class="_card_footer">' +
							'<span class="_card_price"><small>From</small><strong></strong></span>' +
							'<span class="_card_link">View tour →</span>' +
						'</span>' +
					'</div>' +
				'</a>'
			);
			$card.attr('href', tour.url || '#');
			$card.find('.__thumb').toggleClass('_no_photo', !tour.img);
			$card.find('img').attr('src', tour.img || this.fallbackImg).attr('alt', tour.title);
			$card.find('._card_title').text(tour.title);
			$card.find('._card_meta').text([tour.destination, tour.type, tour.duration].filter(Boolean).join(' · '));
			if (tour.badge) {
				$('<span class="_card_badge"></span>').text(tour.badge).appendTo($card.find('._card_meta_row'));
			}
			$card.find('._card_price strong').text(`€${tour.price}`);
			$wrap.append($card);
		});
		return $wrap;
	},
};

export default assistant;
